import VASTError from '../../formats/video-sdk3/lib/vast/vast-error';
import AnalyticsService from './analytics/analytics.service';
import { DisplayEvent } from './analytics/analytics.types';

export const BROWSER_WEBVIEW_CREATED_EVENT = 'browser_webview_created';

let analyticsService: AnalyticsService | null = null;

export function initAnalytics(
  formatName: FormatName,
  config: FormatConfig,
  context: AdContext,
  force: boolean = false
): void {
  analyticsService = new AnalyticsService(formatName, config, context, force);
}

export function getAnalyticsService(): AnalyticsService | null {
  return analyticsService;
}

export function sendMraidAnalyticsTrack(
  closeSource?: string,
  accomplished: boolean = false,
  forceZone?: string,
  eventType: string = 'interaction'
): void {
  analyticsService &&
    analyticsService.sendMraidAnalyticsTrack(
      closeSource,
      accomplished,
      forceZone,
      eventType
    );
}

// TODO: Rather than putting these securities below, we should ensure analyticsService is instantiated before any event is being called.
// This whole file should not exist, and the AnalyticsService should be a singleton.

export function setMediaFile(mediaFile: any): void {
  analyticsService && analyticsService.setMediaFile(mediaFile);
}

// used for output common component which can be in mraid or not
export function cleanAnalyticsStoredEventSending() {
  analyticsService && analyticsService.cleanAnalyticsStoredEventSending();
}

export function sendCreativeClientClick(): void {
  analyticsService && analyticsService.sendCreativeClientClick();
}

export function sendCreativeViewableTrack(): void {
  analyticsService && analyticsService.sendCreativeViewableTrack();
}

export function sendCreativeClientIgnoredClick(
  e?: MouseEvent | MessageEvent
): void {
  analyticsService && analyticsService.sendCreativeClientIgnoredClick(e);
}

export function sendCreativeCloseButtonClick(): void {
  analyticsService && analyticsService.sendCreativeCloseButtonClick();
}

export function sendCreativeOptoutTrack(): void {
  analyticsService && analyticsService.sendCreativeOptoutTrack();
}

export function sendCreativeVideoErrorAutoPlay(): void {
  analyticsService && analyticsService.sendCreativeVideoErrorAutoPlay();
}

export function sendCreativeVideoErrorVastDownload(error: VASTError): void {
  analyticsService &&
    analyticsService.sendCreativeVideoErrorVastDownload(error);
}

export function sendCreativeVideoError(error: MediaError): void {
  analyticsService && analyticsService.sendCreativeVideoError(error);
}

export function sendCreativeVideoErrorPrefetch(): void {
  analyticsService && analyticsService.sendCreativeVideoErrorPrefetch();
}

export function sendCreativeExpand(isExpanded: boolean): void {
  analyticsService && analyticsService.sendCreativeExpand(isExpanded);
}

export function sendCreativeDragged(): void {
  analyticsService && analyticsService.sendCreativeDragged();
}

export function sendCreativeHeaderMinimized(): void {
  analyticsService && analyticsService.sendCreativeHeaderMinimized();
}

export function sendCreativeHeaderMinimizedDefault(): void {
  analyticsService && analyticsService.sendCreativeHeaderMinimizedDefault();
}

export function sendCreativeHeaderAdBelowHeader(): void {
  analyticsService && analyticsService.sendCreativeHeaderAdBelowHeader();
}

export function sendCreativeHeaderAdAboveHeader(): void {
  analyticsService && analyticsService.sendCreativeHeaderAdAboveHeader();
}

export function sendCreativeHeaderOptimized(): void {
  analyticsService && analyticsService.sendCreativeHeaderOptimized();
}

export function sendCreativeLoadInit(): void {
  analyticsService && analyticsService.sendCreativeTrackLoadInit();
}

export function sendCreativeLoadError(): void {
  analyticsService && analyticsService.sendCreativeLoadError();
}

export function disableImpressionTrack(): void {
  analyticsService && analyticsService.disableImpressionTrack();
}

export function disableRagequitTrack(): void {
  analyticsService && analyticsService.disableRagequitTrack();
}

export function setHasVpaid(hasVpaid: boolean): void {
  analyticsService && analyticsService.setHasVpaid(hasVpaid);
}

export function getDisplayTrackUrl(eventType: DisplayEvent | string): string {
  return (
    (analyticsService && analyticsService.getDisplayTrackUrl(eventType)) || ''
  );
}

export function sendImpressionFormat(adContainerStore: any): void {
  analyticsService && analyticsService.sendImpressionFormat(adContainerStore);
}

export function sendMonitoringVideoCompleteEvent(): void {
  analyticsService && analyticsService.sendMonitoringVideoCompleteEvent();
}

export function sendMonitoringVideoChosenEvent(): void {
  analyticsService && analyticsService.sendMonitoringVideoChosenEvent();
}

export function sendMonitoringVideoChosenNewVideoSelectionEvent(): void {
  analyticsService &&
    analyticsService.sendMonitoringVideoChosenNewVideoSelectionEvent();
}

export function sendMonitoringVideoStartedEvent(): void {
  analyticsService && analyticsService.sendMonitoringVideoStartedEvent();
}

export function sendMonitoringVideoVastBlockingEvent(): void {
  analyticsService && analyticsService.sendMonitoringVideoVastBlockingEvent();
}

export function sendMonitoringVideoVastMRCViewableEvent(): void {
  analyticsService &&
    analyticsService.sendMonitoringVideoVastMRCViewableEvent();
}

export function sendMonitoringVideoEvent(eventName: string): void {
  analyticsService && analyticsService.sendVideoMonitoringEvent(eventName);
}

export function sendOmidTrackingEvents(omidTrackingEvents: any): void {
  analyticsService &&
    analyticsService.sendOmidTrackingEvents(omidTrackingEvents);
}

export function sendCreativeVpaidSelected(): void {
  analyticsService && analyticsService.sendCreativeVpaidSelected();
}

export function sendCreativeLoaded(): void {
  analyticsService && analyticsService.sendCreativeLoaded();
}

export function sendEndScreenSelected(endScreen: any): void {
  analyticsService &&
    analyticsService.sendEndScreenTrack(endScreen, 'selected');
}

export function sendEndScreenDisplayed(endScreen: any): void {
  analyticsService &&
    analyticsService.sendEndScreenTrack(endScreen, 'displayed');
}

export function sendEndScreenClicked(endScreen: any): void {
  analyticsService && analyticsService.sendEndScreenTrack(endScreen, 'clicked');
}

export function sendEndScreenClosed(endScreen: any): void {
  analyticsService && analyticsService.sendEndScreenTrack(endScreen, 'closed');
}

export function sendEndScreenAutoClosed(endScreen: any): void {
  analyticsService &&
    analyticsService.sendEndScreenTrack(endScreen, 'auto_closed');
}

export function sendOpenStoreKit(): void {
  analyticsService && analyticsService.sendStoreKitTrack('open');
}

export function sendCloseStoreKit(): void {
  analyticsService && analyticsService.sendStoreKitTrack('close');
}

export function sendErrorStoreKit(errorCode?: number): void {
  analyticsService && analyticsService.sendStoreKitTrack('error', errorCode);
}

export function sendOpenSKOverlay(): void {
  analyticsService && analyticsService.sendStoreKitTrack('overlay_open');
}

export function sendCloseSKOverlay(): void {
  analyticsService && analyticsService.sendStoreKitTrack('overlay_close');
}

export function sendErrorSKOverlay(errorCode?: number): void {
  analyticsService &&
    analyticsService.sendStoreKitTrack('overlay_error', errorCode);
}

export function sendTrack(url: string): void {
  analyticsService && analyticsService.sendTrackUrl(url);
}

export function sendAnalyticsTrack(
  event: Record<string, any>,
  closeSource?: string,
  accomplished: boolean = false,
  forceZone?: string,
  eventType: string = 'interaction',
  delayBeforeInteraction?: number,
  browserParams?: Record<string, any>,
  params?: Record<string, any>
): void {
  analyticsService &&
    analyticsService.sendAnalyticsTrack(
      event,
      closeSource,
      accomplished,
      forceZone,
      eventType,
      delayBeforeInteraction,
      browserParams,
      params
    );
}

export function sendCustomTrack(
  eventType: string,
  delayBeforeInteraction?: number,
  params?: Record<string, any>
): void {
  analyticsService &&
    analyticsService.sendCustomTrack(eventType, delayBeforeInteraction, params);
}

export function rejectDoubleClick(eventType: string): boolean {
  return !!(analyticsService && analyticsService.rejectDoubleClick(eventType));
}

export function checkAndSendNoUserIdTrack(): void {
  analyticsService && analyticsService.checkAndSendNoUserIdTrack();
}

export function waitUntilTracksAreSent(): Promise<unknown> {
  if (!analyticsService) return Promise.resolve(false);
  return analyticsService.waitUntilTracksAreSent();
}

export function sendMonitoringVideoVastTimeoutEvent(): void {
  analyticsService && analyticsService.sendMonitoringVideoVastTimeoutEvent();
}
